import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type RootState } from '@/store';
import AddIcon from '@mui/icons-material/Add';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChatIcon from '@mui/icons-material/Chat';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import RemoveIcon from '@mui/icons-material/Remove';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { LoadingButton } from '@mui/lab';
import {
  alpha,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import {
  getLoanProgramsForMode,
  normalizeLoanProgramForMode,
  USER_LOAN_PROGRAM,
  userLoanProgramMap,
} from '@/config/constants';
import {
  useGetProductQuery,
  useUpdateProductMutation,
} from '@/apis/extended/productApi';
import {
  useGetCurrentUserQuery,
  useUpdateUserLoanInfoMutation,
  useUpdateUserMutation,
} from '@/apis/extended/userApi';
import { ContinueMortgageFileButton } from '@/components/CommercialLoanApplication/ContinueMortgageFileButton';
import { UsdaEligibilityCheck } from '@/components/Property/UsdaEligibilityCheck';
import { PaywallModal } from '@/components/Subscription/PaywallModal';
import { GlassCard } from '@/components/ui/GlassCard';
import { useEstimation } from '@/hooks/useEstimation';
import { useHybridInterestRate } from '@/hooks/useHybridInterestRate';
import { useInvestorAccess } from '@/hooks/useInvestorAccess';
import {
  getBridgeRateByTerm,
  getCurrentInterestRate,
} from '@/utils/calculator/rateIntegration';
import { mapUserLoanProgramToLoanType } from '@/utils/interestRate/quote';
import {
  downPaymentForProduct,
  minDownPaymentPercent,
} from '@/utils/propertyDownPayment';
import { getRefinanceImpliedEquityDown } from '@/utils/refinanceEstimationFromProduct';
import { Loader } from '../Layout/Loader';
import { ProductDetailAIChatModal } from './ProductDetailAIChatModal';
import {
  ProductDetailBasic,
  ProductDetailPurchasePrice,
} from './ProductDetailBasic';
import { ProductDetailComparisonSummary } from './ProductDetailComparisonSummary';
import { ProductDetailDownPaymentModal } from './ProductDetailDownPaymentModal';
import { ProductDetailEnhancedPaymentDisplay } from './ProductDetailEnhancedPaymentDisplay';
import { ProductDetailEstimation } from './ProductDetailEstimation';
import { ProductDetailExploreOfferButton } from './ProductDetailExploreOfferButton';
import { ProductDetailLogos } from './ProductDetailLogos';
import { ProductDetailMonthlyPaymentModal } from './ProductDetailMonthlyPaymentModal';
import { ProductDetailProgress } from './ProductDetailProgress';
import { ProductDetailPropertySpecificsModal } from './ProductDetailPropertySpecificsModal';
import { ProductDetailTourSchedulingModal } from './ProductDetailTourSchedulingModal';
import { ProductDetailValuationModal } from './ProductDetailValuationModal';

type LoanEstimateProps = {
  productId: string;
  /** When true, omits the hero (gallery + listing summary) — used in Deal Room stage 2. */
  hideHero?: boolean;
};

type Comparison = {
  loanProgram: USER_LOAN_PROGRAM;
  downPayment: number;
  /** Months (6/12/18/24) when loanProgram is bridge */
  bridgeTermMonths?: number;
};

// Separate component for Down Payment Controls Divider to properly use hooks
const DownPaymentControlsDivider = ({
  product,
  comparison,
  comparisonIndex,
  currentUser,
  isMobile,
  theme,
  getRateAtLTV,
  handleOpenDownPaymentModal,
  handleSaveDownPaymentAsPrimary,
  handleDownPaymentChange,
  handleDownPaymentCommit,
  handleOpenMonthlyPaymentModal,
}: {
  product: any;
  comparison: Comparison;
  comparisonIndex: number;
  currentUser: any;
  isMobile: boolean;
  theme: any;
  getRateAtLTV: (
    comparison: Comparison,
    ltv: number,
    creditScore: number
  ) => number;
  handleOpenDownPaymentModal: (idx: number, isPercentage: boolean) => void;
  handleSaveDownPaymentAsPrimary: (downPayment: number) => void;
  handleDownPaymentChange: (idx: number, value: number | number[]) => void;
  /** Slider released — the first scenario's value is saved to the property. */
  handleDownPaymentCommit?: (idx: number, value: number | number[]) => void;
  handleOpenMonthlyPaymentModal: (idx: number) => void;
}) => {
  // Create modified product for estimation
  const modifiedProduct = {
    ...product,
    user: {
      ...product.user,
      _id: product.user?._id || '',
      loanProgram: comparison.loanProgram,
      loanInfo: {
        ...product.user?.loanInfo,
        downPayment: comparison.downPayment,
      },
    },
  };
  const { estimation } = useEstimation(
    modifiedProduct as any,
    comparison.downPayment,
    comparison.loanProgram,
    comparison.loanProgram === USER_LOAN_PROGRAM.BRIDGE
      ? comparison.bridgeTermMonths ?? 12
      : undefined
  );
  const monthlyPayment = (estimation as any)?.cleanPayment || 0;

  // Calculate PITIA breakdown
  const monthlyPrincipalInterest = useMemo(() => {
    if (!estimation) return 0;
    if (estimation.type === USER_LOAN_PROGRAM.FIX_AND_FLIP) {
      return (estimation as any).interestPayment || 0;
    }
    return (estimation as any).principalAndInterest || 0;
  }, [estimation]);

  const monthlyPropertyTaxes = (estimation as any)?.monthlyPropertyTaxes || 0;
  const monthlyHomeownersInsurance =
    (estimation as any)?.monthlyHomeownersInsurance || 0;
  const hoaFees = (estimation as any)?.hoaFees || 0;

  // Calculate mortgage insurance
  const monthlyPrivateMortgageInsurance = useMemo(() => {
    if (!estimation) return 0;
    if (estimation.type === USER_LOAN_PROGRAM.DEFAULT) {
      return (estimation as any).monthlyprivateMortgageInsuranceBase || 0;
    }
    return 0;
  }, [estimation]);

  const monthlyMortgageInsurance = useMemo(() => {
    if (!estimation) return 0;
    if (estimation.type === USER_LOAN_PROGRAM.FHA) {
      return (estimation as any).monthlyMIP || 0;
    }
    if (estimation.type === USER_LOAN_PROGRAM.CONVENTIONAL) {
      return (estimation as any).monthlyPMIPayment || 0;
    }
    return 0;
  }, [estimation]);

  const monthlyUSDAFee = useMemo(() => {
    if (!estimation || estimation.type !== USER_LOAN_PROGRAM.USDA) return 0;
    return (estimation as any).monthlyGuaranteeFee || 0;
  }, [estimation]);

  const totalMortgageInsurance =
    monthlyPrivateMortgageInsurance + monthlyMortgageInsurance + monthlyUSDAFee;
  const hasMortgageInsurance = totalMortgageInsurance > 0;

  const borderSubtle = theme.palette.greyShades.border1;
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  // Cash purchase: no loan, so no down payment/LTV/rate — the whole price is
  // paid in cash and the monthly cost is taxes + insurance + HOA.
  const isCash = comparison.loanProgram === USER_LOAN_PROGRAM.CASH;
  // A refinance has no down payment: value minus the new loan is the equity
  // the owner keeps in the home.
  const isRefinance = product.transactionMode === 'refinance';
  const cashPrice = product.loanInfo?.offerPrice ?? product.price;

  return (
    <Box
      sx={{
        borderTop: `1px solid ${borderSubtle}`,
        borderBottom: `1px solid ${borderSubtle}`,
        py: { xs: 2, sm: 2.5, md: 3 },
        mb: { xs: 2, sm: 2.5 },
        mt: { xs: 1.5, sm: 2 },
      }}
    >
      {/* Top Row: Down Payment, Loan Amount, Monthly Payment */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: { xs: 2, sm: 2.5 },
          gap: { xs: 1, sm: 1.5, md: 2 },
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        {/* Down Payment */}
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={600}
              fontSize={{ xs: '0.75rem', sm: '0.875rem' }}
              sx={{ color: textPrimary }}
            >
              {isCash
                ? 'Paid in cash'
                : isRefinance
                ? 'Home equity'
                : 'Down Payment'}
            </Typography>
            {currentUser && !isRefinance && (
              <Tooltip title="Save as primary down payment">
                <span>
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleSaveDownPaymentAsPrimary(comparison.downPayment)
                    }
                    disabled={
                      currentUser.loanInfo?.downPayment ===
                      comparison.downPayment
                    }
                    sx={{
                      color: textSecondary,
                      opacity: 0.85,
                      p: 0.25,
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.08
                        ),
                      },
                      '&.Mui-disabled': {
                        opacity: 0.35,
                      },
                    }}
                  >
                    <SaveIcon sx={{ fontSize: '0.875rem' }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
          <Box
            sx={{
              p: { xs: 1, sm: 1.25, md: 1.5 },
              borderRadius: 1.5,
              border: `1px solid ${borderSubtle}`,
              textAlign: 'center',
            }}
          >
            <Tooltip title="Click to edit dollar amount">
              <Typography
                variant="h6"
                fontWeight={800}
                fontSize={{ xs: '0.875rem', sm: '1rem' }}
                sx={{
                  color: textPrimary,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textDecorationColor: 'transparent',
                  '&:hover': {
                    textDecorationColor: theme.palette.primary.main,
                    opacity: 0.85,
                  },
                }}
                onClick={() =>
                  handleOpenDownPaymentModal(comparisonIndex, false)
                }
              >
                $
                {(isCash ? cashPrice : comparison.downPayment).toLocaleString(
                  'en-US',
                  {
                    maximumFractionDigits: 0,
                  }
                )}
              </Typography>
            </Tooltip>
            <Tooltip title="Click to edit percentage">
              <Typography
                variant="caption"
                fontSize={{ xs: '0.75rem', sm: '0.875rem' }}
                sx={{
                  color: textSecondary,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textDecorationColor: 'transparent',
                  '&:hover': {
                    textDecorationColor: theme.palette.primary.main,
                  },
                }}
                onClick={() =>
                  handleOpenDownPaymentModal(comparisonIndex, true)
                }
              >
                {isCash
                  ? '100% of purchase price'
                  : `${((comparison.downPayment / product.price) * 100).toFixed(
                      1
                    )}% of ${isRefinance ? 'home value' : 'purchase price'}`}
              </Typography>
            </Tooltip>
          </Box>
        </Box>

        {/* Loan Amount */}
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            fontSize={{ xs: '0.75rem', sm: '0.875rem' }}
            gutterBottom
            sx={{ color: textPrimary }}
          >
            Loan Amount
          </Typography>
          <Box
            sx={{
              p: { xs: 1, sm: 1.25, md: 1.5 },
              borderRadius: 1.5,
              border: `1px solid ${borderSubtle}`,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h6"
              fontWeight={800}
              fontSize={{ xs: '0.875rem', sm: '1rem' }}
              sx={{ color: textPrimary }}
            >
              {isCash
                ? 'No loan'
                : `$${(product.price - comparison.downPayment).toLocaleString(
                    'en-US',
                    { maximumFractionDigits: 0 }
                  )}`}
            </Typography>
            <Typography
              variant="caption"
              fontSize={{ xs: '0.75rem', sm: '0.875rem' }}
              sx={{ color: textSecondary }}
            >
              {isCash
                ? 'Cash purchase'
                : `${(
                    ((product.price - comparison.downPayment) / product.price) *
                    100
                  ).toFixed(1)}% LTV`}
            </Typography>
          </Box>
        </Box>

        {/* Monthly Payment */}
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            fontSize={{ xs: '0.75rem', sm: '0.875rem' }}
            gutterBottom
            sx={{ color: textPrimary }}
          >
            {isCash ? 'Monthly costs' : 'Monthly Payment'}
          </Typography>
          <Tooltip title="Click to edit PITIA breakdown">
            <Box
              sx={{
                p: { xs: 1, sm: 1.25, md: 1.5 },
                borderRadius: 1.5,
                border: `1px solid ${borderSubtle}`,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
              onClick={() => handleOpenMonthlyPaymentModal(comparisonIndex)}
            >
              <Typography
                variant="h6"
                fontWeight={800}
                fontSize={{ xs: '0.875rem', sm: '1rem' }}
                sx={{
                  color: textPrimary,
                  textDecoration: 'underline',
                  textDecorationColor: 'transparent',
                  '&:hover': {
                    textDecorationColor: theme.palette.primary.main,
                  },
                }}
              >
                $
                {monthlyPayment.toLocaleString('en-US', {
                  maximumFractionDigits: 0,
                }) || 'N/A'}
              </Typography>
              <Typography
                variant="caption"
                fontSize={{ xs: '0.75rem', sm: '0.875rem' }}
                sx={{ color: textSecondary }}
              >
                {isCash ? 'Taxes + insurance + HOA' : 'PITI'}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* PITIA Breakdown */}
      <Box
        sx={{
          mt: { xs: 1, sm: 1.25 },
          mb: { xs: 1.5, sm: 2 },
          textAlign: 'center',
          background: alpha(theme.palette.primary.main, 0.04),
          borderRadius: 2,
          border: `1px solid ${borderSubtle}`,
          p: { xs: 0.75, sm: 1 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: { xs: 0.75, sm: 1 },
          }}
        >
          <Box
            sx={{ textAlign: 'center', minWidth: { xs: '55px', sm: '65px' } }}
          >
            <Typography
              variant="caption"
              sx={{
                color: textSecondary,
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                display: 'block',
              }}
            >
              P&I
            </Typography>
            <Typography
              variant="caption"
              fontWeight={600}
              fontSize={{ xs: '0.7rem', sm: '0.813rem' }}
              sx={{ color: textPrimary }}
            >
              $
              {monthlyPrincipalInterest.toLocaleString('en-US', {
                maximumFractionDigits: 0,
              }) || '0'}
            </Typography>
          </Box>
          <Box
            sx={{ textAlign: 'center', minWidth: { xs: '55px', sm: '60px' } }}
          >
            <Typography
              variant="caption"
              sx={{
                color: textSecondary,
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                display: 'block',
              }}
            >
              Taxes
            </Typography>
            <Typography
              variant="caption"
              fontWeight={600}
              fontSize={{ xs: '0.7rem', sm: '0.813rem' }}
              sx={{ color: textPrimary }}
            >
              $
              {monthlyPropertyTaxes.toLocaleString('en-US', {
                maximumFractionDigits: 0,
              }) || '0'}
            </Typography>
          </Box>
          <Box
            sx={{ textAlign: 'center', minWidth: { xs: '55px', sm: '60px' } }}
          >
            <Typography
              variant="caption"
              sx={{
                color: textSecondary,
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                display: 'block',
              }}
            >
              Insurance
            </Typography>
            <Typography
              variant="caption"
              fontWeight={600}
              fontSize={{ xs: '0.7rem', sm: '0.813rem' }}
              sx={{ color: textPrimary }}
            >
              $
              {monthlyHomeownersInsurance.toLocaleString('en-US', {
                maximumFractionDigits: 0,
              }) || '0'}
            </Typography>
          </Box>
          <Box
            sx={{ textAlign: 'center', minWidth: { xs: '55px', sm: '60px' } }}
          >
            <Typography
              variant="caption"
              sx={{
                color: textSecondary,
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                display: 'block',
              }}
            >
              HOA
            </Typography>
            <Typography
              variant="caption"
              fontWeight={600}
              fontSize={{ xs: '0.7rem', sm: '0.813rem' }}
              sx={{ color: textPrimary }}
            >
              $
              {hoaFees.toLocaleString('en-US', { maximumFractionDigits: 0 }) ||
                '0'}
            </Typography>
          </Box>
          {hasMortgageInsurance ? (
            <Box
              sx={{ textAlign: 'center', minWidth: { xs: '55px', sm: '60px' } }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: textSecondary,
                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                  display: 'block',
                }}
              >
                MI/PMI
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                fontSize={{ xs: '0.7rem', sm: '0.813rem' }}
                sx={{ color: textPrimary }}
              >
                $
                {totalMortgageInsurance.toLocaleString('en-US', {
                  maximumFractionDigits: 0,
                })}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>

      {/* Rate axis + down payment slider: financed scenarios only */}
      {!isCash && (
        <>
          {/* Rate Indicators */}
          {(() => {
            const creditScore = product.user?.loanInfo?.creditScore || 700;
            const currentLTV =
              (product.price - comparison.downPayment) / product.price;
            const currentRate = getRateAtLTV(
              comparison,
              currentLTV,
              creditScore
            );

            const interval = isMobile ? 10 : 5;
            const ltvIntervals = Array.from(
              { length: Math.floor(95 / interval) },
              (_, i) => (i + 1) * interval
            );

            const sliderMin = Math.max(
              0,
              product.price * minDownPaymentPercent(comparison.loanProgram)
            );
            const sliderMax = product.price * 0.95;
            const sliderRange = sliderMax - sliderMin;

            return (
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: 54, sm: 60, md: 66 },
                  overflow: 'hidden',
                  mb: 0.5,
                  mt: { xs: 0.5, sm: 0.75 },
                }}
              >
                {ltvIntervals.map((ltvPercent) => {
                  const ltv = ltvPercent / 100;
                  const downPaymentForLTV = product.price * (1 - ltv);
                  const rate = getRateAtLTV(comparison, ltv, creditScore);
                  const position =
                    ((downPaymentForLTV - sliderMin) / sliderRange) * 100;
                  const isCurrentLTV = Math.abs(ltv - currentLTV) < 0.02;

                  return (
                    <Box
                      key={ltvPercent}
                      sx={{
                        position: 'absolute',
                        left: `${Math.max(0, Math.min(100, position))}%`,
                        transform: 'translateX(-50%)',
                        top: { xs: 18, sm: 20, md: 22 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        zIndex: isCurrentLTV ? 10 : 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: isCurrentLTV
                            ? { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' }
                            : { xs: '0.5rem', sm: '0.55rem', md: '0.6rem' },
                          fontWeight: isCurrentLTV ? 700 : 400,
                          color: textPrimary,
                          opacity: isCurrentLTV ? 1 : 0.45,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {rate.toFixed(2)}%
                      </Typography>
                      {isCurrentLTV && (
                        <Box
                          sx={{
                            width: 2,
                            height: { xs: 5, sm: 6, md: 7 },
                            bgcolor: theme.palette.primary.main,
                            mt: 0.25,
                          }}
                        />
                      )}
                    </Box>
                  );
                })}

                <Box
                  sx={{
                    position: 'absolute',
                    left: `${Math.max(
                      5,
                      Math.min(
                        95,
                        ((comparison.downPayment - sliderMin) / sliderRange) *
                          100
                      )
                    )}%`,
                    transform: 'translateX(-50%)',
                    top: { xs: 0, sm: 0, md: 0 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    zIndex: 20,
                    maxWidth: { xs: '70px', sm: '85px' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 800,
                      color: theme.palette.primary.contrastText,
                      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                      bgcolor: alpha(theme.palette.primary.main, 0.92),
                      px: { xs: 0.6, sm: 0.75, md: 0.9 },
                      py: { xs: 0.3, sm: 0.4, md: 0.45 },
                      borderRadius: 1,
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                    }}
                  >
                    {currentRate.toFixed(2)}%
                  </Typography>
                </Box>
              </Box>
            );
          })()}

          <Box sx={{ mb: 1.5 }}>
            <Slider
              value={comparison.downPayment}
              onChange={(_, value) =>
                handleDownPaymentChange(comparisonIndex, value)
              }
              onChangeCommitted={(_, value) =>
                handleDownPaymentCommit?.(comparisonIndex, value)
              }
              min={Math.max(
                0,
                product.price * minDownPaymentPercent(comparison.loanProgram)
              )}
              max={product.price * 0.95}
              step={1000}
              sx={{
                '& .MuiSlider-thumb': {
                  backgroundColor: theme.palette.primary.main,
                  width: { xs: 20, sm: 24 },
                  height: { xs: 20, sm: 24 },
                  boxShadow: `0 4px 12px ${alpha(
                    theme.palette.primary.main,
                    0.35
                  )}`,
                  '&:hover': {
                    boxShadow: `0 6px 16px ${alpha(
                      theme.palette.primary.main,
                      0.45
                    )}`,
                  },
                },
                '& .MuiSlider-track': {
                  background: `linear-gradient(90deg, ${
                    theme.palette.primary.main
                  } 0%, ${alpha(theme.palette.primary.main, 0.55)} 100%)`,
                  height: { xs: 5, sm: 6 },
                },
                '& .MuiSlider-rail': {
                  backgroundColor: alpha(theme.palette.text.primary, 0.12),
                  height: { xs: 5, sm: 6 },
                },
              }}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                px: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: textSecondary,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                }}
              >
                $
                {Math.max(
                  0,
                  product.price * minDownPaymentPercent(comparison.loanProgram)
                ).toLocaleString('en-US', {
                  maximumFractionDigits: 0,
                })}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: textSecondary,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                }}
              >
                $
                {(product.price * 0.95).toLocaleString('en-US', {
                  maximumFractionDigits: 0,
                })}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export const LoanEstimate = ({
  productId,
  hideHero = false,
}: LoanEstimateProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { data: product, isLoading } = useGetProductQuery(productId);
  const { data: currentUser } = useGetCurrentUserQuery();
  const [updateUser] = useUpdateUserMutation();
  const [updateUserLoanInfo] = useUpdateUserLoanInfoMutation();
  const [updateProduct] = useUpdateProductMutation();
  const backendRates = useSelector(
    (state: RootState) => state.hybridInterestRate.backendRates
  );

  // Populates state.hybridInterestRate (real rates from GET /interest-rates/current)
  // and the credit-score/LTV adjustment slices that getCurrentInterestRate reads —
  // this is what actually makes the estimate below resolve to a real number. The
  // previous /interest-rates/matrices fetch this replaced had no backing route
  // (always 404) and fed a slice (state.interestRate.rateMatrices) nothing else
  // in this component reads once removed; getCurrentInterestRate already falls
  // back to state.hybridInterestRate.backendRates when no matrix is loaded.
  useHybridInterestRate();

  // Helper function to get minimum down payment by loan type
  const getMinDownPaymentForLoanType = (
    loanType: USER_LOAN_PROGRAM,
    price: number
  ) => {
    const minPercentages: Partial<Record<USER_LOAN_PROGRAM, number>> = {
      [USER_LOAN_PROGRAM.VA]: 0,
      [USER_LOAN_PROGRAM.USDA]: 0,
      [USER_LOAN_PROGRAM.FHA]: 3.5,
      [USER_LOAN_PROGRAM.CONVENTIONAL]: 3,
      [USER_LOAN_PROGRAM.DSCR]: 20,
      [USER_LOAN_PROGRAM.COMMERCIAL]: 25,
      [USER_LOAN_PROGRAM.FIX_AND_FLIP]: 20,
      [USER_LOAN_PROGRAM.BRIDGE]: 20,
    };
    return ((minPercentages[loanType] ?? 5) / 100) * price;
  };

  // Start with empty comparisons, set when product loads
  const [comparisons, setComparisons] = useState<Comparison[]>([]);
  const [propertySpecificsModalOpen, setPropertySpecificsModalOpen] =
    useState(false);

  // Down payment modal state
  const [downPaymentModalOpen, setDownPaymentModalOpen] = useState(false);
  const [downPaymentModalData, setDownPaymentModalData] = useState<{
    optionIndex: number;
    isPercentage: boolean;
    currentValue: number;
    minValue: number;
    maxValue: number;
  } | null>(null);
  const [monthlyPaymentModalOpen, setMonthlyPaymentModalOpen] = useState(false);
  const [monthlyPaymentModalData, setMonthlyPaymentModalData] = useState<{
    optionIndex: number;
  } | null>(null);

  // Valuation modal state
  const [valuationModalOpen, setValuationModalOpen] = useState(false);

  // AI Chat modal state
  const [aiChatModalOpen, setAiChatModalOpen] = useState(false);

  // Tour Scheduling modal state
  const [tourSchedulingModalOpen, setTourSchedulingModalOpen] = useState(false);

  // Investor access and paywall state
  const { checkAccess, hasAccess } = useInvestorAccess();

  const estimateProgramMode =
    product?.transactionMode === 'refinance' ? 'refinance' : 'purchase';
  const loanPrograms = useMemo(
    () => getLoanProgramsForMode(estimateProgramMode, hasAccess),
    [estimateProgramMode, hasAccess]
  );
  const [showPaywall, setShowPaywall] = useState(false);
  const [selectedInvestorProgram, setSelectedInvestorProgram] = useState<
    USER_LOAN_PROGRAM | undefined
  >();
  const [pendingInvestorProgramIndex, setPendingInvestorProgramIndex] =
    useState<number | null>(null);

  // Get Redux state for rate calculations
  const hybridInterestRateState = useSelector(
    (state: RootState) => state.hybridInterestRate
  );
  const interestRateState = useSelector(
    (state: RootState) => state.interestRate
  );

  // Helper function to get rate at a specific LTV
  // This uses the rate matrices from Redux (loaded from backend) for accurate rate calculation
  const getRateAtLTV = useCallback(
    (comparison: Comparison, ltv: number, creditScore: number): number => {
      const loanProgram = comparison.loanProgram;
      const loanType = mapUserLoanProgramToLoanType(loanProgram);

      const minimalState = {
        hybridInterestRate: hybridInterestRateState,
        interestRate: interestRateState,
      } as RootState;

      if (loanProgram === USER_LOAN_PROGRAM.BRIDGE) {
        const term = comparison.bridgeTermMonths ?? 12;
        return getBridgeRateByTerm(minimalState, term, creditScore, ltv);
      }

      const rate = getCurrentInterestRate(
        minimalState,
        loanType as any,
        creditScore,
        ltv
      );

      return rate;
    },
    [hybridInterestRateState, interestRateState]
  );

  // Set initial comparison when product loads
  useEffect(() => {
    if (product && comparisons.length === 0) {
      const mode =
        product.transactionMode === 'refinance' ? 'refinance' : 'purchase';
      const savedProgram =
        (product.refinance?.selection?.program as string | undefined) ||
        product.savedLoanType?.toLowerCase() ||
        product.user?.loanProgram;
      const initialProgram = normalizeLoanProgramForMode(
        savedProgram,
        mode,
        hasAccess
      );
      const initialDownPayment =
        mode === 'refinance'
          ? getRefinanceImpliedEquityDown(product)
          : downPaymentForProduct(product, { loanProgram: initialProgram });

      setComparisons([
        {
          loanProgram: initialProgram,
          downPayment: initialDownPayment,
          ...(initialProgram === USER_LOAN_PROGRAM.BRIDGE
            ? { bridgeTermMonths: 12 }
            : {}),
        },
      ]);
    }
  }, [product, comparisons.length, hasAccess]);

  // Keep comparison #0 in sync when hero / API updates per-listing savedDownPayment.
  useEffect(() => {
    if (!product?.savedDownPayment || comparisons.length === 0) return;
    // A refinance's loan amount comes from its intake, not a down payment.
    if (product.transactionMode === 'refinance') return;
    setComparisons((prev) => {
      if (prev[0]?.downPayment === product.savedDownPayment) return prev;
      return [
        { ...prev[0], downPayment: product.savedDownPayment! },
        ...prev.slice(1),
      ];
    });
  }, [product?.savedDownPayment, product?._id, comparisons.length]);

  // Handler functions that use hooks must be defined before early returns
  const handleOpenMonthlyPaymentModal = useCallback((optionIndex: number) => {
    setMonthlyPaymentModalData({ optionIndex });
    setMonthlyPaymentModalOpen(true);
  }, []);

  const handleSaveMonthlyPayment = useCallback(
    async (values: {
      monthlyPropertyTaxes?: number;
      monthlyHomeownersInsurance?: number;
      hoaFees?: number;
    }) => {
      if (!product) return;
      try {
        await updateProduct({
          id: product._id,
          monthlyPropertyTaxes: values.monthlyPropertyTaxes,
          monthlyHomeownersInsurance: values.monthlyHomeownersInsurance,
          hoaFees: values.hoaFees,
        });
      } catch (error) {
        console.error('Failed to save monthly payment values:', error);
      }
    },
    [product, updateProduct]
  );

  const handleResetMonthlyPayment = useCallback(async () => {
    if (!product) return;
    try {
      await updateProduct({
        id: product._id,
        monthlyPropertyTaxes: null as unknown as number | undefined,
        monthlyHomeownersInsurance: null as unknown as number | undefined,
        hoaFees: null as unknown as number | undefined,
      });
      setMonthlyPaymentModalOpen(false);
    } catch (error) {
      console.error('Failed to reset monthly payment values:', error);
    }
  }, [product, updateProduct]);

  const handleBridgeTermMonthsChange = useCallback(
    (idx: number, months: number) => {
      setComparisons((prev) =>
        prev.map((c, i) => (i === idx ? { ...c, bridgeTermMonths: months } : c))
      );
    },
    []
  );

  if (isLoading || !product || comparisons.length === 0)
    return <Loader isFull />;

  const handleLoanProgramChange = async (
    idx: number,
    value: USER_LOAN_PROGRAM
  ) => {
    // Check if this is an investor-only loan program
    const investorPrograms = ['dscr', 'bridge', 'commercial', 'fix_and_flip'];
    const requiresInvestorAccess = investorPrograms.includes(
      value.toLowerCase()
    );

    if (requiresInvestorAccess && !checkAccess(value)) {
      // Show paywall modal
      setSelectedInvestorProgram(value);
      setPendingInvestorProgramIndex(idx);
      setShowPaywall(true);
      return;
    }

    setComparisons((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, loanProgram: value } : c))
    );

    // Save to product if this is the first comparison
    if (idx === 0 && product) {
      try {
        await updateProduct({
          id: product._id,
          savedLoanType: value,
        });
      } catch (error) {
        console.error('Failed to save loan type:', error);
      }
    }
  };
  const handleDownPaymentChange = (idx: number, value: number | number[]) => {
    setComparisons((prev) =>
      prev.map((c, i) =>
        i === idx
          ? { ...c, downPayment: Array.isArray(value) ? value[0] : value }
          : c
      )
    );
  };

  // Scenario #1 is the property's down payment everywhere else (Stage 1,
  // cards, offer form, pre-qual letter) — save it when the slider is released.
  const handleDownPaymentCommit = async (
    idx: number,
    value: number | number[]
  ) => {
    if (idx !== 0 || !product) return;
    const next = Math.round(Array.isArray(value) ? value[0] : value);
    if (next === product.savedDownPayment) return;
    try {
      await updateProduct({ id: product._id, savedDownPayment: next });
    } catch (error) {
      console.error('Failed to save down payment:', error);
    }
  };

  const handleAddComparison = () => {
    if (comparisons.length < 3 && product) {
      // Use saved down payment or fall back to user's default
      const defaultDownPayment =
        product.transactionMode === 'refinance'
          ? getRefinanceImpliedEquityDown(product)
          : downPaymentForProduct(product, {
              loanProgram: USER_LOAN_PROGRAM.CONVENTIONAL,
            });

      setComparisons((prev) => [
        ...prev,
        {
          loanProgram: USER_LOAN_PROGRAM.CONVENTIONAL,
          downPayment: defaultDownPayment,
        },
      ]);
    }
  };
  const handleRemoveComparison = (idx: number) => {
    setComparisons((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleOpenDownPaymentModal = (
    optionIndex: number,
    isPercentage: boolean
  ) => {
    if (!product) return;

    const currentComp = comparisons[optionIndex];
    const currentValue = isPercentage
      ? (currentComp.downPayment / product.price) * 100
      : currentComp.downPayment;

    // Use loan-specific minimum down payment
    const minDownPaymentAmount = getMinDownPaymentForLoanType(
      currentComp.loanProgram,
      product.price
    );
    const minValue = isPercentage
      ? (minDownPaymentAmount / product.price) * 100
      : minDownPaymentAmount;
    const maxValue = isPercentage ? 95 : product.price * 0.95;

    setDownPaymentModalData({
      optionIndex,
      isPercentage,
      currentValue,
      minValue,
      maxValue,
    });
    setDownPaymentModalOpen(true);
  };

  const handleSaveDownPayment = async (newValue: number) => {
    if (!downPaymentModalData || !product) return;

    const { optionIndex, isPercentage } = downPaymentModalData;
    const newDownPayment = isPercentage
      ? (newValue / 100) * product.price
      : newValue;

    setComparisons((prev) =>
      prev.map((comp, idx) =>
        idx === optionIndex ? { ...comp, downPayment: newDownPayment } : comp
      )
    );

    // Save to product if this is the first comparison
    if (optionIndex === 0) {
      try {
        await updateProduct({
          id: product._id,
          savedDownPayment: newDownPayment,
        });
      } catch (error) {
        console.error('Failed to save down payment:', error);
      }
    }
  };

  const handleSaveLoanTypeAsPrimary = async (
    loanProgram: USER_LOAN_PROGRAM
  ) => {
    if (!currentUser) return;

    try {
      await updateUser({
        loanProgram: loanProgram,
      }).unwrap();

      toast.success(
        `${
          userLoanProgramMap[loanProgram] ?? loanProgram
        } is now your default loan program`
      );
    } catch (error) {
      console.error('Failed to save loan type:', error);
    }
  };

  const handleSaveDownPaymentAsPrimary = async (downPayment: number) => {
    if (!currentUser) return;

    try {
      await updateUserLoanInfo({
        downPayment: downPayment,
      }).unwrap();

      // Show success feedback (you can add a toast notification here)
      console.log(`Down payment ${downPayment} saved as primary down payment`);
    } catch (error) {
      console.error('Failed to save down payment:', error);
    }
  };

  const getLoanProgramColor = (program: USER_LOAN_PROGRAM) => {
    switch (program) {
      case USER_LOAN_PROGRAM.CONVENTIONAL:
        return theme.palette.primary.main;
      case USER_LOAN_PROGRAM.FHA:
        return '#4caf50';
      case USER_LOAN_PROGRAM.VA:
        return '#ff9800';
      case USER_LOAN_PROGRAM.USDA:
        return '#9c27b0';
      case USER_LOAN_PROGRAM.DSCR:
        return '#f44336';
      case USER_LOAN_PROGRAM.COMMERCIAL:
        return '#e91e63';
      case USER_LOAN_PROGRAM.FIX_AND_FLIP:
        return '#795548';
      default:
        return '#757575';
    }
  };

  const mockEstimations = comparisons.map((comp) => {
    const purchasePrice = product?.loanInfo?.offerPrice || product.price;
    const loanAmount = purchasePrice - comp.downPayment;
    const ltv = purchasePrice > 0 ? loanAmount / purchasePrice : 0;
    const creditScore = product.user?.loanInfo?.creditScore || 700;
    const interestRate = getRateAtLTV(comp, ltv, creditScore);
    return {
      monthlyPayment: Math.round((product.price - comp.downPayment) * 0.006),
      cashToClose:
        comp.downPayment +
        Math.round((product.price - comp.downPayment) * 0.03),
      loanAmount: product.price - comp.downPayment,
      interestRate,
    };
  });

  return (
    <Container
      maxWidth="xl"
      className="taste-rise"
      sx={{
        py: 4,
        px: { xs: 2, sm: 3, md: 4 },
        overflow: 'hidden',
      }}
    >
      {/* Header Section — lives on Deal Room stage 1 when hideHero */}
      {!hideHero && (
        <Box sx={{ mb: 6 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} lg={6}>
              <ProductDetailLogos product={product} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <ProductDetailBasic product={product} />
            </Grid>
            <Grid item xs={12}>
              <ProductDetailPurchasePrice product={product} />
            </Grid>
            <Grid
              item
              xs={12}
              lg={6}
              sx={{ display: { xs: 'none', lg: 'block' } }}
            />
            <Grid item xs={12} lg={6}>
              <ProductDetailExploreOfferButton product={product} />
              <ProductDetailProgress
                product={product}
                footer={
                  <ContinueMortgageFileButton
                    productId={productId}
                    product={product}
                  />
                }
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Enhanced Comparison UI */}
      <Box
        sx={{
          mb: 6,
          overflow: 'hidden',
        }}
      >
        <GlassCard
          variant="medium"
          sx={{
            border: `2px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            borderRadius: 4,
            overflow: 'hidden',
            width: '100%',
            '@media (hover: hover)': {
              '&:hover': {
                transform: 'translateY(-2px)',
              },
            },
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Grid
              container
              spacing={{ xs: 2, sm: 3 }}
              sx={{
                minHeight: 400,
                width: '100%',
                margin: 0,
              }}
            >
              {/* Action Buttons */}
              <Grid item xs={12}>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  {/* Property Specifics Button */}
                  <Grid item xs={6} sm={6} md={4} lg={3}>
                    <Card
                      onClick={() => setPropertySpecificsModalOpen(true)}
                      sx={{
                        background: `linear-gradient(135deg, ${alpha(
                          theme.palette.primary.main,
                          0.06
                        )} 0%, ${alpha(
                          theme.palette.primary.main,
                          0.02
                        )} 100%)`,
                        border: `2px solid ${theme.palette.greyShades.border1}`,
                        borderRadius: 4,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        height: 'fit-content',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 24px ${alpha(
                            theme.palette.common.black,
                            0.1
                          )}`,
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          textAlign: 'center',
                          p: { xs: 2, sm: 3 },
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                        }}
                      >
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();
                            setPropertySpecificsModalOpen(true);
                          }}
                          sx={{
                            mb: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            width: { xs: 48, sm: 56 },
                            height: { xs: 48, sm: 56 },
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.16),
                              color: theme.palette.primary.dark,
                              transform: 'scale(1.1)',
                            },
                          }}
                        >
                          <SettingsIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                        </IconButton>
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          gutterBottom
                          sx={{
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            color: theme.palette.text.primary,
                          }}
                        >
                          Property Specifics
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 180,
                            color: theme.palette.text.secondary,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          }}
                        >
                          Adjust taxes, insurance & fees
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* AI Insights Button */}
                  <Grid item xs={6} sm={6} md={4} lg={3}>
                    <Card
                      onClick={() => setAiChatModalOpen(true)}
                      sx={{
                        background: `linear-gradient(135deg, ${alpha(
                          theme.palette.primary.main,
                          0.06
                        )} 0%, ${alpha(
                          theme.palette.primary.main,
                          0.02
                        )} 100%)`,
                        border: `2px solid ${theme.palette.greyShades.border1}`,
                        borderRadius: 4,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        height: 'fit-content',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 24px ${alpha(
                            theme.palette.common.black,
                            0.1
                          )}`,
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          textAlign: 'center',
                          p: { xs: 2, sm: 3 },
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                        }}
                      >
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();
                            setAiChatModalOpen(true);
                          }}
                          sx={{
                            mb: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            width: { xs: 48, sm: 56 },
                            height: { xs: 48, sm: 56 },
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.16),
                              color: theme.palette.primary.dark,
                              transform: 'scale(1.1)',
                            },
                          }}
                        >
                          <ChatIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                        </IconButton>
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          gutterBottom
                          sx={{
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            color: theme.palette.text.primary,
                          }}
                        >
                          AI Insights
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 180,
                            color: theme.palette.text.secondary,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          }}
                        >
                          Get AI-powered loan recommendations
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Valuation Report Button */}
                  <Grid item xs={6} sm={6} md={4} lg={3}>
                    <Card
                      onClick={() => setValuationModalOpen(true)}
                      sx={{
                        background: `linear-gradient(135deg, ${alpha(
                          theme.palette.primary.main,
                          0.06
                        )} 0%, ${alpha(
                          theme.palette.primary.main,
                          0.02
                        )} 100%)`,
                        border: `2px solid ${theme.palette.greyShades.border1}`,
                        borderRadius: 4,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        height: 'fit-content',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 24px ${alpha(
                            theme.palette.common.black,
                            0.1
                          )}`,
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          textAlign: 'center',
                          p: { xs: 2, sm: 3 },
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                        }}
                      >
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();
                            setValuationModalOpen(true);
                          }}
                          sx={{
                            mb: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            width: { xs: 48, sm: 56 },
                            height: { xs: 48, sm: 56 },
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.16),
                              color: theme.palette.primary.dark,
                              transform: 'scale(1.1)',
                            },
                          }}
                        >
                          <AssessmentIcon
                            sx={{ fontSize: { xs: 24, sm: 28 } }}
                          />
                        </IconButton>
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          gutterBottom
                          sx={{
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            color: theme.palette.text.primary,
                          }}
                        >
                          Valuation Report
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 180,
                            color: theme.palette.text.secondary,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          }}
                        >
                          Buy a comprehensive property valuation
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Property Tour Scheduling Button */}
                  <Grid item xs={6} sm={6} md={4} lg={3}>
                    <Card
                      onClick={() => setTourSchedulingModalOpen(true)}
                      sx={{
                        background: `linear-gradient(135deg, ${alpha(
                          theme.palette.primary.main,
                          0.06
                        )} 0%, ${alpha(
                          theme.palette.primary.main,
                          0.02
                        )} 100%)`,
                        border: `2px solid ${theme.palette.greyShades.border1}`,
                        borderRadius: 4,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        height: 'fit-content',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 24px ${alpha(
                            theme.palette.common.black,
                            0.1
                          )}`,
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          textAlign: 'center',
                          p: { xs: 2, sm: 3 },
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                        }}
                      >
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();
                            setTourSchedulingModalOpen(true);
                          }}
                          sx={{
                            mb: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            width: { xs: 48, sm: 56 },
                            height: { xs: 48, sm: 56 },
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.16),
                              color: theme.palette.primary.dark,
                              transform: 'scale(1.1)',
                            },
                          }}
                        >
                          <CalendarTodayIcon
                            sx={{ fontSize: { xs: 24, sm: 28 } }}
                          />
                        </IconButton>
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          gutterBottom
                          sx={{
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            color: theme.palette.text.primary,
                          }}
                        >
                          Schedule Tour
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 180,
                            color: theme.palette.text.secondary,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          }}
                        >
                          Tour this property + comparable
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              {comparisons.map((comp, idx) => (
                <Grid
                  item
                  xs={12}
                  sm={comparisons.length === 1 ? 12 : 6}
                  md={comparisons.length === 1 ? 12 : 12 / comparisons.length}
                  lg={comparisons.length === 1 ? 12 : 12 / comparisons.length}
                  xl={comparisons.length === 1 ? 12 : 12 / comparisons.length}
                  key={idx}
                  sx={{
                    display: 'flex',
                    '& > *': { width: '100%' },
                  }}
                >
                  <GlassCard
                    variant="medium"
                    sx={{
                      border: `3px solid ${getLoanProgramColor(
                        comp.loanProgram
                      )}`,
                      borderRadius: 3,
                      position: 'relative',
                      overflow: 'visible',
                      height: 'fit-content',
                      '@media (hover: hover)': {
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          borderColor: getLoanProgramColor(comp.loanProgram),
                        },
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${getLoanProgramColor(
                          comp.loanProgram
                        )} 0%, ${alpha(
                          getLoanProgramColor(comp.loanProgram),
                          0.7
                        )} 100%)`,
                        borderRadius: '12px 12px 0 0',
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 1.5, sm: 2, md: 3 },
                        pt: { xs: 2.5, sm: 3, md: 4 },
                        width: '100%',
                      }}
                    >
                      <Chip
                        label={comp.loanProgram.toUpperCase()}
                        size="small"
                        sx={{
                          mb: { xs: 1.5, sm: 2, md: 3 },
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.text.primary,
                          fontWeight: 600,
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                          alignSelf: 'flex-start',
                        }}
                      />

                      {/* Program Selector Controls - Compact Row with Divider */}
                      <Box
                        sx={{
                          borderTop: `1px solid ${theme.palette.greyShades.border1}`,
                          borderBottom: `1px solid ${theme.palette.greyShades.border1}`,
                          py: { xs: 1.5, sm: 2 },
                          mb: { xs: 2, sm: 2.5 },
                          mt: { xs: 1, sm: 1.5 },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 1, sm: 1.5 },
                            flexWrap: { xs: 'wrap', sm: 'nowrap' },
                          }}
                        >
                          {/* Remove Option Button */}
                          {comparisons.length > 1 && (
                            <Tooltip title="Remove this option">
                              <IconButton
                                color="error"
                                onClick={() => handleRemoveComparison(idx)}
                                size="small"
                                sx={{
                                  bgcolor: alpha(theme.palette.error.main, 0.1),
                                  color: theme.palette.error.main,
                                  minWidth: { xs: 40, sm: 44 },
                                  height: { xs: 40, sm: 44 },
                                  '&:hover': {
                                    bgcolor: theme.palette.error.main,
                                    color: theme.palette.error.contrastText,
                                  },
                                }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Change Loan Program Selector */}
                          <FormControl
                            sx={{
                              flex: 1,
                              minWidth: { xs: 120, sm: 150 },
                            }}
                          >
                            <InputLabel
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                '&.Mui-focused': {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            >
                              Change Loan Program
                            </InputLabel>
                            <Select
                              value={comp.loanProgram}
                              label="Change Loan Program"
                              onChange={(e) =>
                                handleLoanProgramChange(
                                  idx,
                                  e.target.value as USER_LOAN_PROGRAM
                                )
                              }
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                fontSize: { xs: '0.813rem', sm: '0.875rem' },
                                height: { xs: 40, sm: 44 },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: theme.palette.greyShades.border1,
                                  borderWidth: 2,
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: theme.palette.primary.main,
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                  {
                                    borderColor: theme.palette.primary.main,
                                    borderWidth: 2,
                                  },
                                '& .MuiSelect-icon': {
                                  color: theme.palette.text.secondary,
                                },
                              }}
                            >
                              {loanPrograms
                                // "Default" isn't a program — hide it unless an
                                // older estimate still has it selected.
                                .filter(
                                  (program) =>
                                    program !== USER_LOAN_PROGRAM.DEFAULT ||
                                    comp.loanProgram ===
                                      USER_LOAN_PROGRAM.DEFAULT
                                )
                                // Hide USDA once the customer's check found
                                // this address ineligible (unless selected).
                                .filter(
                                  (program) =>
                                    program !== USER_LOAN_PROGRAM.USDA ||
                                    product.usdaEligibility?.status !==
                                      'ineligible' ||
                                    comp.loanProgram === USER_LOAN_PROGRAM.USDA
                                )
                                .map((program) => (
                                  <MenuItem
                                    key={program}
                                    value={program}
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {userLoanProgramMap[program] ?? program}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>

                          {/* Make-default button: the dropdown already saves the
                              program for this property; this sets the profile
                              default used for new properties. */}
                          {currentUser && (
                            <Tooltip
                              title={
                                currentUser.loanProgram === comp.loanProgram
                                  ? 'This is your default loan program for new properties.'
                                  : 'Use this loan program by default for new properties. (Your choice above is already saved for this property.)'
                              }
                              arrow
                            >
                              <span>
                                <LoadingButton
                                  variant="outlined"
                                  size="small"
                                  startIcon={<SaveIcon fontSize="small" />}
                                  onClick={() =>
                                    handleSaveLoanTypeAsPrimary(
                                      comp.loanProgram
                                    )
                                  }
                                  disabled={
                                    currentUser.loanProgram === comp.loanProgram
                                  }
                                  sx={{
                                    color: theme.palette.primary.main,
                                    py: { xs: 0.5, sm: 0.75 },
                                    px: { xs: 1, sm: 1.5 },
                                    fontSize: { xs: '0.75rem', sm: '0.813rem' },
                                    height: { xs: 40, sm: 44 },
                                    whiteSpace: 'nowrap',
                                    borderColor: alpha(
                                      theme.palette.primary.main,
                                      0.45
                                    ),
                                    '&:hover': {
                                      borderColor: theme.palette.primary.main,
                                      backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.06
                                      ),
                                    },
                                    '&.Mui-disabled': {
                                      borderColor:
                                        theme.palette.greyShades.border1,
                                      color: theme.palette.text.disabled,
                                    },
                                  }}
                                >
                                  {currentUser.loanProgram === comp.loanProgram
                                    ? 'My default'
                                    : 'Make my default'}
                                </LoadingButton>
                              </span>
                            </Tooltip>
                          )}

                          {/* Add Another Option Button */}
                          {comparisons.length < 3 && (
                            <Tooltip title="Add another option">
                              <span>
                                <LoadingButton
                                  variant="outlined"
                                  size="small"
                                  startIcon={<AddIcon fontSize="small" />}
                                  onClick={handleAddComparison}
                                  sx={{
                                    color: theme.palette.primary.main,
                                    py: { xs: 0.5, sm: 0.75 },
                                    px: { xs: 1, sm: 1.5 },
                                    fontSize: { xs: '0.75rem', sm: '0.813rem' },
                                    height: { xs: 40, sm: 44 },
                                    whiteSpace: 'nowrap',
                                    borderColor: alpha(
                                      theme.palette.primary.main,
                                      0.5
                                    ),
                                    '&:hover': {
                                      borderColor: theme.palette.primary.main,
                                      backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.1
                                      ),
                                    },
                                  }}
                                >
                                  Add Option
                                </LoadingButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>

                      {comp.loanProgram === USER_LOAN_PROGRAM.BRIDGE && (
                        <FormControl
                          size="small"
                          fullWidth
                          sx={{ mt: 1.5, maxWidth: 260 }}
                        >
                          <InputLabel id={`bridge-term-label-${idx}`}>
                            Bridge term
                          </InputLabel>
                          <Select
                            labelId={`bridge-term-label-${idx}`}
                            label="Bridge term"
                            value={comp.bridgeTermMonths ?? 12}
                            onChange={(e) =>
                              handleBridgeTermMonthsChange(
                                idx,
                                Number(e.target.value)
                              )
                            }
                          >
                            {[6, 12, 18, 24].map((m) => (
                              <MenuItem key={m} value={m}>
                                {m} months
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}

                      {comp.loanProgram === USER_LOAN_PROGRAM.USDA && (
                        <UsdaEligibilityCheck
                          productId={product._id}
                          eligibility={product.usdaEligibility}
                        />
                      )}

                      {/* Comprehensive Down Payment & Loan Controls Divider */}
                      <DownPaymentControlsDivider
                        product={product}
                        comparison={comp}
                        comparisonIndex={idx}
                        currentUser={currentUser}
                        isMobile={isMobile}
                        theme={theme}
                        getRateAtLTV={getRateAtLTV}
                        handleOpenDownPaymentModal={handleOpenDownPaymentModal}
                        handleSaveDownPaymentAsPrimary={
                          handleSaveDownPaymentAsPrimary
                        }
                        handleDownPaymentChange={handleDownPaymentChange}
                        handleDownPaymentCommit={handleDownPaymentCommit}
                        handleOpenMonthlyPaymentModal={
                          handleOpenMonthlyPaymentModal
                        }
                      />
                    </CardContent>
                  </GlassCard>
                </Grid>
              ))}
            </Grid>
            {/* Full Width Loan Comparisons */}
            <ProductDetailEstimation
              product={product}
              comparisons={comparisons}
              onBridgeTermMonthsChange={handleBridgeTermMonthsChange}
            />
          </CardContent>
        </GlassCard>
      </Box>
      {/* Comparison Summary */}
      {comparisons.length > 1 && (
        <ProductDetailComparisonSummary
          comparisons={comparisons}
          estimations={mockEstimations}
        />
      )}

      {/* Results Section */}

      {/* Property Specifics Modal */}
      <ProductDetailPropertySpecificsModal
        product={product}
        open={propertySpecificsModalOpen}
        onClose={() => setPropertySpecificsModalOpen(false)}
      />

      {/* Down Payment Modal */}
      {downPaymentModalData && (
        <ProductDetailDownPaymentModal
          open={downPaymentModalOpen}
          onClose={() => setDownPaymentModalOpen(false)}
          currentValue={downPaymentModalData.currentValue}
          maxValue={downPaymentModalData.maxValue}
          minValue={downPaymentModalData.minValue}
          onSave={handleSaveDownPayment}
          isPercentage={downPaymentModalData.isPercentage}
          productPrice={product?.price}
        />
      )}

      {/* Monthly Payment Modal */}
      {monthlyPaymentModalData && product && (
        <ProductDetailMonthlyPaymentModal
          open={monthlyPaymentModalOpen}
          onClose={() => setMonthlyPaymentModalOpen(false)}
          product={product}
          comparison={comparisons[monthlyPaymentModalData.optionIndex]}
          onSave={handleSaveMonthlyPayment}
          onReset={handleResetMonthlyPayment}
        />
      )}

      {/* Valuation Modal */}
      <ProductDetailValuationModal
        open={valuationModalOpen}
        onClose={() => setValuationModalOpen(false)}
        propertyPrice={product?.price}
      />

      {/* AI Chat Modal */}
      <ProductDetailAIChatModal
        open={aiChatModalOpen}
        onClose={() => setAiChatModalOpen(false)}
        productPrice={product?.price}
      />

      {/* Tour Scheduling Modal */}
      <ProductDetailTourSchedulingModal
        open={tourSchedulingModalOpen}
        onClose={() => setTourSchedulingModalOpen(false)}
        propertyPrice={product?.price}
      />

      {/* Paywall Modal for Investor Access */}
      <PaywallModal
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        selectedLoanProgram={selectedInvestorProgram}
        onUpgraded={() => {
          setShowPaywall(false);
          if (pendingInvestorProgramIndex !== null && selectedInvestorProgram) {
            handleLoanProgramChange(
              pendingInvestorProgramIndex,
              selectedInvestorProgram
            );
          }
          setPendingInvestorProgramIndex(null);
        }}
      />
    </Container>
  );
};

export const ProductDetail = LoanEstimate;
